import { useMemo } from "react";
import axios from "axios";
import { Message } from "../../ai/ai.types";
import { AiClient, OutputAndSource, useAiWithRag } from "../../ai/contexts/ai.rag.context";
import { useTranslation } from "@me8eon/translation";
import parseLinks from "../utils/parseLinks";
import { EnvironmentConfig, LLM_MODEL } from "@me8eon/config";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { FeatureFlags } from "@me8eon/featureflags/src/feature.flags";
import i18next from "i18next";
import { QuickActionType } from "../../../modules/AiWidgets/widget.types";

export type TIssueList = string[]
export type TIssueListLanguage = {
    [key: string]: TIssueList;
}
type TAllowedFeedbackStrings = "default" | "create-incident";

type TInternetSearchMessage = {
    bot?: string;
    user: string;
};

export interface MessageContentInterface {
    alice: string;
    messageTitle: string;
    messageContent: string;
    icon: string;
    issueList: TIssueList;
    feedback?: TAllowedFeedbackStrings[];
    disableSearch?: boolean;
    cardPosition: number;
    label?: string;
    handleAiQuery: (
        messages: Message[],
        query: string,
        action?: string,
        quickActionType?: QuickActionType,
        data?: any,
        ff?: FeatureFlags
    ) => Promise<OutputAndSource>;
}

export interface IAiAssistData {
    [key: string]: MessageContentInterface;
}

const serviceIssueList: TIssueListLanguage = {
    en: [
        "I forgot my password. How can I reset it?",
        "I'm having trouble sending/receiving emails. What should I do?",
    ],
    de: [
        "Ich habe mein Passwort vergessen. Wie kann ich es zurücksetzen?",
        "Ich habe Probleme beim Senden/Empfangen von E-Mails. Was sollte ich tun?",
    ],
};

const generalIssueList: TIssueListLanguage = {
    en: [
        "What are the EON values?",
        "Can you help me draft a professional email requesting a meeting with a client?",
    ],
    de: [
        "Was sind die EON-Werte?",
        "Können Sie mir beim Verfassen einer professionellen E-Mail mit der Bitte um ein Treffen mit einem Kunden helfen?",
    ],
};

export const indexToNameMap: Record<string, string> = {
    "azureblob": "E.ON Knowledge Base",
    "office": "E.ON Knowledge Base",
    "servicenow": `E.ON IT Knowledge Base`,
    "confluence": `E.ON Confluence`,
    "jira": `E.ON Jira`,
    "sharepoint": `E.ON SharePoint`,
    "mygenius": "E.ON MyGenius",
    "order-it": "E.ON My IT Order",
    "apps": "E.ON Apps",
    "hr": "HR Policies",
    "compass": "Compass",
    "default": `General knowledge`,
};

const useAiData = (): IAiAssistData => {
    const ai: AiClient = useAiWithRag();
    const { t, i18n } = useTranslation();
    const language = i18next.languages[0];
    const jwt = useJwtToken();
    const { featureFlagEnabled } = useUserData();


    return {
        selfService: {
            alice: "selfService",
            messageTitle: t("chatBot.selfServiceTitle"),
            messageContent: t("chatBot.selfServiceContent"),
            icon: "itSupport",
            feedback: ["default", "create-incident"],
            issueList: serviceIssueList[language],
            disableSearch: false,
            cardPosition: 2,
            label: t("chatBot.askEon"),
            handleAiQuery: async (messages: Message[], query: string, action?: string, quickActionType?: QuickActionType, data?: any, ff?: FeatureFlags) => {
                try {
                    return action == "user-query" ? await ai.handleRagQuery(messages, query, language, featureFlagEnabled, action, ff).then((response: OutputAndSource) => {
                        return ({
                            ...response,
                            output: `${parseLinks(response.output)}`,
                        })
                    }
                    ) : ai.handleQuickActions(messages, query, quickActionType, data).then((response: OutputAndSource) => ({
                        ...response,
                        output: `${parseLinks(response.output)}`,
                    })
                    )
                } catch (err) {
                    console.error(err);
                    return {
                        output: "Oops! Something went wrong, please try rephrasing your query or come back a bit later.",
                        source: "internet",
                        action: "rag-eon-gpt",
                    };
                }
            }
        },
        internetSearch: {
            alice: "internetSearch",
            messageTitle: t("chatBot.internetSearchTitle"),
            messageContent: t("chatBot.internetSearchContent"),
            icon: "internetSearch",
            issueList: generalIssueList[language],
            feedback: ["default"],
            disableSearch: true,
            cardPosition: 3,
            label: t("chatBot.askInternet"),
            handleAiQuery: async (messages: Message[], query: string) => {
                try {
                    const history: TInternetSearchMessage[] = [];
                    messages.forEach((message, index) => {
                        if (index % 2 === 0) {
                            history.push({ user: message.content as string });
                        } else {
                            history[history.length - 1].bot = message.content as string;
                        }
                    });
                    history.push({ user: query });
                    const queryBody = {
                        history,
                        overrides: {
                            retrieval_mode: "hybrid",
                            semantic_ranker: true,
                            semantic_captions: false,
                            top: 3,
                            language_model: LLM_MODEL,
                        },
                    };

                    const response = await axios({
                        method: "post",
                        url: `${EnvironmentConfig.genAiApiBaseUrl}/genai/ama/me/ask`,
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${await jwt()}`,
                        },
                        data: queryBody,
                    });

                    const withReplacedLinks = response.data.answer.replace(
                        /genai-eon-public\//g,
                        `${EnvironmentConfig.genAiApiBaseUrl}/genai/ama/docs/me/download_by_name/`
                    );

                    return {
                        output: `${parseLinks(withReplacedLinks)} <br />`,
                        source: "internet",
                        action: "rag-eon-gpt",
                    };
                } catch (err) {
                    console.error(err);
                    return {
                        output: "This service is not available at the moment, please try again later.",
                        source: "internet",
                        action: "rag-eon-gpt",
                    };
                }
            },
        },
    };
};

export default useAiData;
